import { useState, useEffect, useRef } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import { Base64UploadAdapter } from "ckeditor5";
import {
  ClassicEditor,
  AccessibilityHelp,
  Autoformat,
  BlockQuote,
  Bold,
  Essentials,
  FontBackgroundColor,
  FontColor,
  FontSize,
  Heading,
  ImageBlock,
  ImageCaption,
  ImageInsert,
  ImageResize,
  ImageStyle,
  ImageTextAlternative,
  ImageToolbar,
  ImageUpload,
  Italic,
  Link,
  List,
  MediaEmbed,
  Paragraph,
  PasteFromOffice,
  SourceEditing,
  SpecialCharacters,
  Subscript,
  Superscript,
  Table,
  TableToolbar,
  Underline,
  Undo,
  Alignment
} from "ckeditor5";

import "ckeditor5/ckeditor5.css";

const CKEditorComponent = ({ value = "", onChange, disabled = false }) => {
  const [isLayoutReady, setIsLayoutReady] = useState(false);

  useEffect(() => {
    setIsLayoutReady(true);
    return () => setIsLayoutReady(false);
  }, []);

  const editorConfig = {
    toolbar: {
      items: [
        "undo",
        "redo",
        "|",
        "sourceEditing",
        "|",
        "heading",
        "|",
        "alignment",
        "|",
        "fontSize",
        "fontColor",
        "fontBackgroundColor",
        "|",
        "bold",
        "italic",
        "underline",
        "subscript",
        "superscript",
        "|",
        "specialCharacters",
        "link",
        "insertImage",
        "mediaEmbed",
        "insertTable",
        "blockQuote",
        "|",
        "bulletedList",
        "numberedList",
        "outdent",
        "indent"
      ],
      shouldNotGroupWhenFull: false
    },

    plugins: [
      AccessibilityHelp,
      Autoformat,
      BlockQuote,
      Bold,
      Essentials,
      FontBackgroundColor,
      FontColor,
      FontSize,
      Heading,
      ImageBlock,
      ImageCaption,
      ImageInsert,
      ImageResize,
      ImageStyle,
      ImageTextAlternative,
      ImageToolbar,
      ImageUpload,
      Base64UploadAdapter,
      Italic,
      Link,
      List,
      MediaEmbed,
      Paragraph,
      PasteFromOffice,
      SourceEditing,
      SpecialCharacters,
      Subscript,
      Superscript,
      Table,
      TableToolbar,
      Underline,
      Undo,
      Alignment
    ],

    /**
     * ✅ INLINE FONT SIZE CONFIG
     */
    fontSize: {
      options: [
        10,
        12,
        14,
        16,
        18,
        20,
        22,
        24,
        28,
        32,
        36,
        "default"
      ],
      supportAllValues: true
    },

    /**
     * ✅ BLOCK-ONLY HEADINGS (CORRECT)
     */
    heading: {
      options: [
        { model: "paragraph", title: "Paragraph", class: "ck-heading_paragraph" },
        { model: "heading1", view: "h1", title: "Heading 1" },
        { model: "heading2", view: "h2", title: "Heading 2" },
        { model: "heading3", view: "h3", title: "Heading 3" },
        { model: "heading4", view: "h4", title: "Heading 4" },
        { model: "heading5", view: "h5", title: "Heading 5" },
        { model: "heading6", view: "h6", title: "Heading 6" }
      ]
    },

    image: {
      styles: [
        "alignLeft",
        "alignCenter",
        "alignRight",
        "inline",
        "wrapText",
        "breakText"
      ],
      toolbar: [
        "imageStyle:alignLeft",
        "imageStyle:alignCenter",
        "imageStyle:alignRight",
        "|",
        "toggleImageCaption",
        "imageTextAlternative",
        "|",
        "resizeImage"
      ]
    },

    table: {
      contentToolbar: [
        "tableColumn",
        "tableRow",
        "mergeTableCells",
        "tableProperties",
        "tableCellProperties"
      ]
    },

    alignment: {
      options: ["left", "center", "right", "justify"]
    },

    placeholder: "Type or paste your content here!"
  };

  const handleEditorChange = (event, editor) => {
    const data = editor.getData();
    onChange?.(data || "<p></p>");
  };

  return (
    <div style={{ opacity: disabled ? 0.5 : 1 }}>
      {isLayoutReady && (
        <CKEditor
          editor={ClassicEditor}
          data={value}
          config={editorConfig}
          disabled={disabled}
          onChange={handleEditorChange}
        />
      )}
    </div>
  );
};

export default CKEditorComponent;
